/**
 * useEstimateQueries.ts
 * 
 * Standalone React Query hooks for Estimates, Pricebook, and Service Templates.
 * 
 * EstimatesContext.tsx internally delegates to these hooks for backward compatibility.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useIdioma } from '../i18n/idioma';
import { useTallerListo } from './useTallerListo';
import { Estimate, PricebookItem, ServiceTemplate, PrivateTemplate } from '../types';
import {
    fetchEstimates, addEstimateDoc, updateEstimateDoc, deleteEstimateDoc,
    fetchPricebook, addPricebookItemDoc, updatePricebookItemDoc, deletePricebookItemDoc,
    fetchTemplates, addTemplateDoc, updateTemplateDoc,
    fetchPrivateTemplates, addPrivateTemplate, deletePrivateTemplate, updatePrivateTemplate
} from '../services/database';

export const ESTIMATE_KEYS = {
    estimates: (wsId: string) => ['estimates', wsId] as const,
    pricebook: (wsId: string) => ['pricebook', wsId] as const,
    templates: (wsId: string) => ['templates', wsId] as const,
    privateTemplates: (wsId: string) => ['privateTemplates', wsId] as const,
};

// Pass wsId explicitly into the queryFn instead of letting database.ts read it
// from localStorage. On first sign-in, WorkspaceContext's localStorage-persist
// useEffect runs *after* React Query's queryFn fires (parent effects fire after
// child effects), so getWorkspaceId() would return the stale 'default' value
// and the query would silently resolve with an empty list. Reading wsId from
// the closure here keeps the value in sync with the queryKey React Query uses.
//
// La puerta es `useTallerListo`, la misma que proyectos, flota, servicios y
// reloj. Aquí seguía `isAuthorized && !!activeWorkspace?.id`, que siempre es
// cierto (el respaldo tiene id 'default'): estas tres consultas disparaban con
// el taller en 'error' y en la landing, y fueron las que más informes dejaron
// cuando una sesión se perdió por debajo de la app.
export function useEstimatesQuery() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: ESTIMATE_KEYS.estimates(wsId),
        queryFn: () => fetchEstimates(wsId),
        enabled: listo,
    });
}

export function usePricebook() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: ESTIMATE_KEYS.pricebook(wsId),
        queryFn: () => fetchPricebook(wsId),
        enabled: listo,
    });
}

export function useServiceTemplates() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: ESTIMATE_KEYS.templates(wsId),
        queryFn: () => fetchTemplates(wsId),
        enabled: listo,
    });
}

// Mutations

export function useAddEstimate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, data }: { id: string, data: Partial<Estimate> }) => {
            await addEstimateDoc(id, data);
            return id;
        },
        onMutate: async ({ id, data }) => {
            await qc.cancelQueries({ queryKey: ['estimates'] });
            const previousData = qc.getQueriesData({ queryKey: ['estimates'] });
            qc.setQueriesData({ queryKey: ['estimates'] }, (old: Estimate[] | undefined) => {
                if (!old) return [{ ...(data as Estimate), id }];
                return [{ ...(data as Estimate), id }, ...old];
            });
            return { previousData };
        },
        onError: (_err, _vars, context) => {
            const ctx = context as { previousData?: Array<[unknown, unknown]> } | undefined;
            if (ctx?.previousData) {
                ctx.previousData.forEach(([key, data]) => {
                    qc.setQueryData(key as readonly unknown[], data);
                });
            }
        },
        onSettled: () => qc.invalidateQueries({ queryKey: ['estimates'] }),
    });
}

/** Marca las escrituras de estimados para poder contarlas en `onSettled`. */
export const UPDATE_ESTIMATE_KEY = ['estimate:update'] as const;

export interface UpdateEstimateVars {
    id: string;
    updates: Partial<Estimate>;
    /**
     * El llamante ya avisa del fallo por su cuenta, así que esta mutación se
     * calla. Lo usan los caminos que abren su propio diálogo (aceptar/rechazar
     * una propuesta); sin esto el usuario vería dos mensajes del mismo error.
     */
    silencioso?: boolean;
}

export function useUpdateEstimate() {
    const qc = useQueryClient();
    const { t } = useIdioma();
    return useMutation({
        mutationKey: UPDATE_ESTIMATE_KEY,
        mutationFn: async ({ id, updates }: UpdateEstimateVars) => {
            await updateEstimateDoc(id, updates);
        },
        // Optimistic update — patch the cache before the network round-trip so
        // controlled inputs (proposal title, scope textarea, etc.) reflect the
        // user's keystrokes instantly instead of waiting for Supabase to ack.
        onMutate: async ({ id, updates }) => {
            await qc.cancelQueries({ queryKey: ['estimates'] });
            const previousData = qc.getQueriesData({ queryKey: ['estimates'] });
            qc.setQueriesData({ queryKey: ['estimates'] }, (old: Estimate[] | undefined) => {
                if (!old) return old;
                return old.map(e =>
                    e.id === id
                        ? { ...e, ...updates, lastUpdated: new Date().toISOString() }
                        : e
                );
            });
            return { previousData };
        },
        /**
         * Antes esto solo restauraba la caché, en silencio absoluto: ni toast, ni
         * log. En el editor de propuestas el efecto era desconcertante — escribías
         * el alcance, se caía la red, y el texto SE BORRABA SOLO en pantalla sin
         * que nadie explicara por qué. Parecía que la app perdía el trabajo.
         *
         * El rollback sigue siendo lo correcto (la pantalla no debe mentir sobre
         * lo que hay guardado); lo que faltaba era decirlo.
         */
        onError: (err, vars, context) => {
            // Roll back to the snapshot if the server rejects the write
            const ctx = context as { previousData?: Array<[unknown, unknown]> } | undefined;
            if (ctx?.previousData) {
                ctx.previousData.forEach(([key, data]) => {
                    qc.setQueryData(key as readonly unknown[], data);
                });
            }
            console.error('[useUpdateEstimate] write failed:', err);
            if (vars?.silencioso) return;
            // Qué pasó y qué hacer. El mensaje del servidor no se enseña: al
            // contratista no le sirve un código de Postgres.
            toast.error(t('estimados.error.noSeGuardo'), { duration: 6000 });
        },
        // L-5: reconcile the cache with the server once the write settles. The
        // optimistic patch covers the happy path, but a server-side trigger,
        // derived column, or a concurrent writer (e.g. the public accept/decline
        // RPC, the follow-up engine) can diverge the row — invalidate so the next
        // read is authoritative instead of silently drifting until a window-focus
        // refetch. Mirrors useAddEstimate / useDeleteEstimate.
        //
        // Solo invalida la ÚLTIMA escritura de una ráfaga. Con varias en vuelo, el
        // refetch de la primera vuelve con la fila anterior a las siguientes y pisa
        // el parche optimista: el editor de propuestas «rebotaba» —arrastrabas el
        // deslizador del encuadre y saltaba hacia atrás solo. En `onSettled` esta
        // mutación todavía cuenta como activa, así que >1 significa que quedan más.
        onSettled: () => {
            if (qc.isMutating({ mutationKey: UPDATE_ESTIMATE_KEY }) > 1) return;
            qc.invalidateQueries({ queryKey: ['estimates'] });
        },
    });
}

export function useDeleteEstimate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await deleteEstimateDoc(id);
        },
        onMutate: async (id) => {
            await qc.cancelQueries({ queryKey: ['estimates'] });
            const previousData = qc.getQueriesData({ queryKey: ['estimates'] });
            qc.setQueriesData({ queryKey: ['estimates'] }, (old: Estimate[] | undefined) => {
                if (!old) return old;
                return old.filter(e => e.id !== id);
            });
            return { previousData };
        },
        onError: (_err, _vars, context) => {
            const ctx = context as { previousData?: Array<[unknown, unknown]> } | undefined;
            if (ctx?.previousData) {
                ctx.previousData.forEach(([key, data]) => {
                    qc.setQueryData(key as readonly unknown[], data);
                });
            }
        },
        onSettled: () => qc.invalidateQueries({ queryKey: ['estimates'] }),
    });
}

export function useAddPricebookItem() {
    const qc = useQueryClient();
    return useMutation({
        // Return the new row's id so callers (Add Material modal) can stamp
        // it onto the EstimateItem they drop, which lets persistPricebookEdits
        // upsert future cost edits to the same row.
        mutationFn: async (item: Omit<PricebookItem, 'id' | 'lastUpdated'>): Promise<string> => {
            return await addPricebookItemDoc(item);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['pricebook'] }),
    });
}

export function useUpdatePricebookItem() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<PricebookItem> }) => {
            await updatePricebookItemDoc(id, updates);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['pricebook'] }),
    });
}

export function useDeletePricebookItem() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await deletePricebookItemDoc(id);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['pricebook'] }),
    });
}

export function useAddServiceTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (template: Omit<ServiceTemplate, 'id'>) => {
            await addTemplateDoc(template);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
    });
}

export function useUpdateServiceTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<ServiceTemplate> }) => {
            await updateTemplateDoc(id, updates);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
    });
}

export function usePrivateTemplates() {
    const { wsId, listo } = useTallerListo();

    return useQuery({
        queryKey: ESTIMATE_KEYS.privateTemplates(wsId),
        queryFn: () => fetchPrivateTemplates(wsId),
        enabled: listo,
    });
}

export function useDeletePrivateTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await deletePrivateTemplate(id);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['privateTemplates'] }),
    });
}

// T-2: create a private (line) template from scratch — standalone creation, not via
// an estimate. Used by the Templates page "New line template" flow.
export function useAddPrivateTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ name, items }: { name: string; items: any[] }) => {
            return addPrivateTemplate(name, items, null);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['privateTemplates'] }),
    });
}

export function useUpdatePrivateTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, updates }: { id: string; updates: Partial<PrivateTemplate> }) => {
            await updatePrivateTemplate(id, updates);
        },
        // Optimistic patch — the editor drawer relies on the card refreshing
        // before the network round-trip so closing the drawer immediately
        // shows the new state.
        onMutate: async ({ id, updates }) => {
            await qc.cancelQueries({ queryKey: ['privateTemplates'] });
            const previousData = qc.getQueriesData({ queryKey: ['privateTemplates'] });
            qc.setQueriesData({ queryKey: ['privateTemplates'] }, (old: PrivateTemplate[] | undefined) => {
                if (!old) return old;
                return old.map(t => (t.id === id ? { ...t, ...updates } : t));
            });
            return { previousData };
        },
        onError: (_err, _vars, context) => {
            const ctx = context as { previousData?: Array<[unknown, unknown]> } | undefined;
            if (ctx?.previousData) {
                ctx.previousData.forEach(([key, data]) => {
                    qc.setQueryData(key as readonly unknown[], data);
                });
            }
        },
        onSettled: () => qc.invalidateQueries({ queryKey: ['privateTemplates'] }),
    });
}
